/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import block from 'propmods';
// eslint-disable-next-line @typescript-eslint/no-use-before-define
import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { Button, RadioButton, TextArea } from 'lego-on-react';
import * as keyset from 'i18n/SkillPublishing';
import i18n from '@yandex-int/i18n';
import { Operation, OperationEvent } from '../../model/operation';
import { Channel, DeveloperType, LookType, MonitoringType, Skill } from '../../model/skill';
import { DraftControlPanel } from '../DraftControlPanel/DraftControlPanel';
import Events from '../Events/Events';
import { H3, PageAside2, PageHeadline, PageMain2 } from '../Typo/Typo';
import { Omit, PublishingPageDangerousOperation } from '../../types';
import * as SnapshotContext from '../../context/snapshotContext';
import { updateSkillFlags, deleteApp, stopApp, forceStopApp } from '../../api';
import { tycoon } from '../../utils/messages';
import { routes } from '../../lib/routes';
import { Handles as ConfirmHandles, UnmanagedConfirm } from '../Confirm/UnmanagedConfirm';
import CopyField from '../CopyField/CopyField';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { statusDescriptions, statusDescriptionsAired, dangerousOperations } from '../../locale/ru/publishingPage';
import { commonComments } from '../../locale/ru/operations';

import './PublishingPage.scss';
import Banners from '../Banners/Banners';
const t = i18n(keyset);

type BoolFlags = keyof Pick<
    Skill,
    | 'exposeInternalFlags'
    | 'isRecommended'
    | 'openInNewTab'
    | 'isVip'
    | 'useZora'
    | 'isTrustedSmartHomeSkill'
    | 'safeForKids'
>;
type EnumFlags = keyof Pick<Skill, 'monitoringType' | 'developerType' | 'look'>;
type SkillFlag = EnumFlags | BoolFlags;

export type OnFlagUpdate = (skillId: string, flag: SkillFlag, patch: Partial<Skill>) => void;

export interface PublishingPageProps {
    skill: Skill;
    operations: Operation[];
}

export interface PublishingPageRouteProps {
    skillId?: string;
}

const b = block('PublishingPage');

const getStatusDescription = (skill: Skill) => {
    const { channel, status } = skill.draft;

    if (skill.onAir) {
        return (statusDescriptionsAired as any)[channel][status];
    }
    return (statusDescriptions as any)[channel][status];
};

function newest(aEvent: OperationEvent<any>, bEvent: OperationEvent<any>) {
    if (aEvent.operation.createdAt > bEvent.operation.createdAt) {
        return -1;
    }
    if (bEvent.operation.createdAt > aEvent.operation.createdAt) {
        return 1;
    }
    return 0;
}

interface FlagProps<T extends SkillFlag> {
    name: T;
    title: string;
    onChange: (flag: T, value: Skill[T]) => void;
    values: Record<T extends EnumFlags ? Skill[T] : string, string>;
    value: Skill[T];
    disabled?: boolean;
}

const createFlag =
    <T extends SkillFlag>({ stringToValue }: { stringToValue: (_str: string) => Skill[T] }) =>
    (props: FlagProps<T>) => (
        <div>
            <h4>{props.title}</h4>
            <RadioButton
                name={props.name}
                theme="normal"
                size="s"
                onChange={(e) => props.onChange(props.name, stringToValue(e.target.value))}
                value={String(props.value)}
                disabled={props.disabled}
            >
                {Object.entries<string>(props.values).map(([key, title]) => (
                    <RadioButton.Radio key={key} value={key}>
                        {title}
                    </RadioButton.Radio>
                ))}
            </RadioButton>
        </div>
    );

const BoolComponent = createFlag<BoolFlags>({
    stringToValue: (x) => x === 'true',
});

const BoolFlag = (
    props: Omit<FlagProps<BoolFlags>, 'values'> & {
        trueLabel: string;
        falseLabel: string;
    },
) => <BoolComponent {...props} values={{ true: props.trueLabel, false: props.falseLabel }} />;

type EnumFlagOuterProps = 'onChange' | 'value' | 'disabled';

const createEnumFlag = <T extends EnumFlags>(args: Omit<FlagProps<T>, EnumFlagOuterProps>) => {
    const Component = createFlag<T>({
        stringToValue: (s) => s as Skill[T],
    });

    // eslint-disable-next-line react/display-name
    return (props: Pick<FlagProps<T>, EnumFlagOuterProps>) => <Component {...args} {...props} />;
};

const MonitoringTypeFlag = createEnumFlag({
    name: 'monitoringType',
    title: t('monitoring-group'),
    values: {
        [MonitoringType.Yandex]: t('yandex'),
        [MonitoringType.JustAI]: 'JustAI',
        [MonitoringType.Nonmonitored]: t('no-monitoring'),
    },
});

const DeveloperTypeFlag = createEnumFlag({
    name: 'developerType',
    title: t('developer-type'),
    values: {
        [DeveloperType.External]: t('external'),
        [DeveloperType.Yandex]: t('yandex'),
    },
});

const LookFlag = createEnumFlag({
    name: 'look',
    title: t('appearance'),
    values: {
        [LookType.External]: t('external'),
        [LookType.Internal]: t('internal'),
    },
});

type StopSkillProps = {
    onStop: () => void;
};
const StopSkill = ({ onStop }: StopSkillProps) => {
    const [text, setText] = useState('');
    return (
        <div>
            <h4>{t('to-stop-dialog')}</h4>
            <TextArea
                id="stop-skill-comment"
                size="s"
                text={text}
                onChange={setText}
                theme="normal"
                rows={4}
                placeholder={t('comment')}
            />
            <Button onClick={onStop} theme="normal" size="s">
                {t('stop')}
            </Button>
        </div>
    );
};

type CombinedProps = PublishingPageProps & RouteComponentProps<PublishingPageRouteProps>;

interface State {
    isFlagUpdating: Record<SkillFlag, boolean>;
}

class PublishingPage extends React.Component<CombinedProps, State> {
    public static contextType = SnapshotContext.Context;
    public confirmRef = React.createRef<ConfirmHandles>();

    public state: State = {
        isFlagUpdating: {
            developerType: false,
            exposeInternalFlags: false,
            isRecommended: false,
            isVip: false,
            look: false,
            monitoringType: false,
            openInNewTab: false,
            useZora: false,
            isTrustedSmartHomeSkill: false,
            safeForKids: false,
        },
    };

    public render() {
        const { refreshSnapshot } = this.context as SnapshotContext.WithSnapshot;
        const { skill } = this.props;
        const { isFlagUpdating } = this.state;

        const handleDeleteSkillClick = () => {
            this.confirmRef.current?.open({
                title: t('delete-dialog'),
                description: t('delete-dialog-confirmation'),
                onConfirm: async () => {
                    await deleteApp(skill.id, skill.channel);
                    this.props.history.push(routes.app.root);
                    tycoon.sendChatDeletion(skill.id);

                    await refreshSnapshot();
                },
            });
        };

        const handleStopSkillClick = () => {
            this.confirmRef.current?.open({
                title: t('stop-dialog'),
                description: t('stop-dialog-confirmation'),
                onConfirm: async () => {
                    await stopApp(skill.id, skill.channel);
                    await refreshSnapshot();
                },
            });
        };

        const events: OperationEvent<React.ReactNode>[] = this.props.operations
            .map((operation) => ({
                skillEntity: {
                    id: skill.id,
                    name: skill.name,
                    channel: skill.channel,
                },
                operation: {
                    ...operation,
                    comment: operation.comment || commonComments[operation.type],
                },
            }))
            .sort(newest);

        const onFlagChange = <T extends SkillFlag>(flag: T, value: Skill[T]) =>
            this.onFlagUpdate(skill.id, flag, {
                [flag]: value,
            });

        const boolFlagProps = (name: BoolFlags) => ({
            name,
            value: skill[name],
            disabled: isFlagUpdating[name],
        });

        return (
            <ErrorBoundary withHomepageLink>
                <div {...b(this.props)}>
                    <PageHeadline title={t('headline')} />

                    <PageMain2>
                        <div {...b('jumbo')}>{getStatusDescription(skill)}</div>
                    </PageMain2>
                    <PageAside2>
                        {skill.isBanned ? (
                            t('dialog-banned')
                        ) : (
                            <DraftControlPanel settingsAreValid={skill.draft.isAllowedForDeploy} safeToRequestReview />
                        )}
                        {__appConfig.user.isAdmin && (
                            <div {...b('admin')}>
                                <BoolFlag
                                    title={t('recommended-skill')}
                                    falseLabel={t('no')}
                                    trueLabel={t('yes')}
                                    onChange={onFlagChange}
                                    {...boolFlagProps('isRecommended')}
                                />
                                <DeveloperTypeFlag
                                    value={skill.developerType}
                                    onChange={onFlagChange}
                                    disabled={isFlagUpdating.developerType}
                                />
                                <BoolFlag
                                    title={t('trusted-skill')}
                                    falseLabel={t('no')}
                                    trueLabel={t('yes')}
                                    onChange={onFlagChange}
                                    {...boolFlagProps('isTrustedSmartHomeSkill')}
                                />
                                {skill.channel === Channel.AliceSkill && (
                                    <div>
                                        <BoolFlag
                                            title={t('title-access-web-hook')}
                                            falseLabel={t('checkbox-directly')}
                                            trueLabel={t('checkbox-zora')}
                                            onChange={onFlagChange}
                                            {...boolFlagProps('useZora')}
                                        />
                                        <BoolFlag
                                            title={t('title-experimental')}
                                            falseLabel={t('hide')}
                                            trueLabel={t('show')}
                                            onChange={onFlagChange}
                                            {...boolFlagProps('exposeInternalFlags')}
                                        />
                                        <BoolFlag
                                            title={t('title-open-new-tab')}
                                            falseLabel={t('no')}
                                            trueLabel={t('yes')}
                                            onChange={onFlagChange}
                                            {...boolFlagProps('openInNewTab')}
                                        />
                                        <BoolFlag
                                            title={t('title-safe-kids')}
                                            falseLabel={t('no')}
                                            trueLabel={t('yes')}
                                            onChange={onFlagChange}
                                            {...boolFlagProps('safeForKids')}
                                        />

                                        <MonitoringTypeFlag
                                            value={skill.monitoringType}
                                            onChange={onFlagChange}
                                            disabled={isFlagUpdating.monitoringType}
                                        />
                                        <LookFlag
                                            value={skill.look}
                                            onChange={onFlagChange}
                                            disabled={isFlagUpdating.look}
                                        />
                                        <StopSkill onStop={this.onStop} />
                                    </div>
                                )}
                            </div>
                        )}
                        <Banners />
                    </PageAside2>
                    <div {...b('events')}>
                        <H3>{t('events-history')}</H3>
                        <Events showHeader events={events} />
                    </div>

                    <div {...b('info')}>
                        <H3>{t('dialog-id')}</H3>
                        <CopyField text={skill.id} buttonLabel={t('copy')} label="" />
                    </div>

                    <div {...b('dangerZone')}>
                        <H3>{t('danger-zone')}</H3>
                        <div {...b('dangerousOps')}>
                            {skill.onAir && (
                                <div {...b('dangerousOp')}>
                                    <div {...b('dangerousOpTitle')}>{t('stop-dialog')}</div>
                                    <div {...b('dangerousOpDescription')}>
                                        {dangerousOperations[skill.channel][PublishingPageDangerousOperation.Stop]}
                                    </div>
                                    <div {...b('dangerousOpButton')}>
                                        <Button theme="normal" size="s" onClick={handleStopSkillClick}>
                                            {t('to-stop-dialog')}
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <div {...b('dangerousOp')}>
                                <div {...b('dangerousOpTitle')}>{t('delete-dialog')}</div>
                                <div {...b('dangerousOpDescription')}>
                                    {dangerousOperations[skill.channel][PublishingPageDangerousOperation.Delete]}
                                </div>
                                <div {...b('dangerousOpButton')}>
                                    <Button theme="normal" size="s" onClick={handleDeleteSkillClick}>
                                        {t('to-delete-dialog')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <UnmanagedConfirm ref={this.confirmRef} />
                </div>
            </ErrorBoundary>
        );
    }

    private onStop = () => {
        const comment = document.getElementById('stop-skill-comment')?.textContent;
        if (!comment) {
            window.alert('Комментарий не должен быть пустым');
            return;
        }
        if (!window.confirm('ВЫ УВЕРЕНЫ, ЧТО ХОТИТЕ ОСТАНОВИТЬ ДАННЫЙ НАВЫК?')) {
            return;
        }
        forceStopApp(this.props.skill.id, this.props.skill.channel, { comment });
    };

    private onFlagUpdate: OnFlagUpdate = async (skillId, flag, patch) => {
        const { refreshSnapshot } = this.context as SnapshotContext.WithSnapshot;

        this.setState({
            isFlagUpdating: {
                ...this.state.isFlagUpdating,
                [flag]: true,
            },
        });

        try {
            await updateSkillFlags(skillId, patch);
            await refreshSnapshot();
        } catch (e) {
            console.error(e);
        } finally {
            this.setState((prevState) => ({
                isFlagUpdating: {
                    ...prevState.isFlagUpdating,
                    [flag]: false,
                },
            }));
        }
    };
}

export default PublishingPage;
